import { useMemo } from 'react';
import {
  UseTableFooterParams,
  UseTableFooterReturn,
  TableFooterHelpers,
} from '../types/footer-types';

/**
 * Hook for managing table footer calculations and state
 *
 * This hook provides:
 * - Summary value calculations based on configuration
 * - Helper functions for common aggregations
 * - Auto-detection of numeric columns
 * - Memoized calculations for performance
 *
 * @param params - Configuration and data for footer calculations
 * @returns Footer state and helper functions
 */
export function useTableFooter<T extends Record<string, unknown>>(
  params: UseTableFooterParams<T>,
): UseTableFooterReturn<T> {
  const { rows, config } = params;

  // Helper functions for common calculations
  const helpers: TableFooterHelpers = useMemo(
    () => ({
      sum: (values: (number | null)[]): number => {
        return values.reduce<number>((acc, val) => acc + (val || 0), 0);
      },

      average: (values: (number | null)[]): number => {
        const validValues = values.filter((val): val is number => val !== null);
        if (validValues.length === 0) return 0;
        return (
          validValues.reduce((acc, val) => acc + val, 0) / validValues.length
        );
      },

      min: (values: (number | null)[]): number => {
        const validValues = values.filter((val): val is number => val !== null);
        if (validValues.length === 0) return 0;
        return Math.min(...validValues);
      },

      max: (values: (number | null)[]): number => {
        const validValues = values.filter((val): val is number => val !== null);
        if (validValues.length === 0) return 0;
        return Math.max(...validValues);
      },

      count: (values: (number | null)[]): number => {
        return values.filter((val): val is number => val !== null).length;
      },

      formatNumber: (
        value: number,
        options?: Intl.NumberFormatOptions,
      ): string => {
        try {
          return new Intl.NumberFormat(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
            ...options,
          }).format(value);
        } catch {
          return value.toString();
        }
      },
    }),
    [],
  );

  // Auto-detect numeric columns
  const numericColumns = useMemo(() => {
    if (!config.autoDetectNumericColumns || rows.length === 0) {
      return [];
    }

    const sampleRow = rows[0];
    const numericCols: Array<keyof T> = [];

    Object.keys(sampleRow).forEach((key) => {
      const value = sampleRow[key];
      if (typeof value === 'number' && !isNaN(value)) {
        numericCols.push(key as keyof T);
      }
    });

    return numericCols;
  }, [rows, config.autoDetectNumericColumns]);

  // Extract column values for calculations
  const getColumnValues = useMemo(() => {
    return (columnId: keyof T): (number | null)[] => {
      return rows.map((row) => {
        const value = row[columnId];
        if (typeof value === 'number' && !isNaN(value)) {
          return value;
        }
        return null;
      });
    };
  }, [rows]);

  // Calculate summary values
  const summaryValues = useMemo(() => {
    return config.summaryColumns.map((summaryCol) => {
      const columnValues = getColumnValues(summaryCol.columnId);
      const value = summaryCol.callback(rows, columnValues, helpers);

      return {
        columnId: summaryCol.columnId,
        label: summaryCol.label,
        value,
      };
    });
  }, [config.summaryColumns, getColumnValues, rows, helpers]);

  // Total rows count and label
  const totalRowsCount = rows.length;
  const totalRowsLabel = config.totalRowsLabel || 'Total Items';

  return {
    summaryValues,
    totalRowsCount,
    totalRowsLabel,
    helpers,
    numericColumns,
  };
}
